import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";
import { UploadSimple, FileXls, X, CheckCircle, Spinner, Robot } from "@phosphor-icons/react";
import { api, fmtUSD, fmtCompact, fmtPct } from "../lib/api";

// Simulated agent progress narration shown WHILE the real `/api/spend/import`
// request is in-flight. The backend returns the authoritative agent_trail
// post-completion; this gives the user real-time feedback.
const PROGRESS_STAGES = [
  { agent: "Data Hygiene Agent",     detail: "Decoding workbook · sniffing CSV/XLSX encoding",          pct:  8 },
  { agent: "Data Hygiene Agent",     detail: "Normalizing supplier names · stripping LLC/Inc suffixes", pct: 22 },
  { agent: "Data Hygiene Agent",     detail: "Coercing amounts · USD conversion · null elimination",    pct: 38 },
  { agent: "Technical Agent",        detail: "Mapping UNSPSC segments · 8-digit family lookup",         pct: 58 },
  { agent: "Technical Agent",        detail: "Direct / Indirect classification · maverick detection",    pct: 74 },
  { agent: "Strategic Partner Lead", detail: "Composing executive rollup · top-vendor concentration",   pct: 88 },
  { agent: "Context Ledger",         detail: "Committing digest to Mongo · refreshing COP",             pct: 96 },
];

export default function SpendImporterModal({ open, onOpenChange, onImported }) {
  const [file, setFile]       = useState(null);
  const [stageIdx, setStageIdx] = useState(0);
  const [running, setRunning]   = useState(false);
  const [result, setResult]     = useState(null);
  const [error, setError]       = useState(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setFile(null); setRunning(false); setResult(null); setError(null); setStageIdx(0);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  }, [open]);

  const handleFile = (f) => {
    if (!f) return;
    const ok = /\.(csv|xlsx|xlsm|txt)$/i.test(f.name);
    if (!ok) {
      toast.error("Unsupported file type", { description: "Upload CSV or XLSX." });
      return;
    }
    setFile(f);
    setError(null);
    setResult(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  const runImport = async () => {
    if (!file) return;
    setRunning(true); setStageIdx(0); setError(null); setResult(null);
    // Cycle through narration stages so the user sees motion immediately
    timerRef.current = setInterval(() => {
      setStageIdx((i) => Math.min(i + 1, PROGRESS_STAGES.length - 1));
    }, 850);
    try {
      const digest = await api.spendImport(file);
      setResult(digest);
      toast.success("Spend Imported", {
        description: `${digest.row_count.toLocaleString()} rows · ${fmtUSD(digest.total_spend)} normalized`,
        duration: 6000,
      });
      onImported && onImported(digest);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Import failed";
      setError(detail);
      toast.error("Import Failed", { description: detail });
    } finally {
      clearInterval(timerRef.current);
      setRunning(false);
      setStageIdx(PROGRESS_STAGES.length - 1);
    }
  };

  const stage = PROGRESS_STAGES[stageIdx] || PROGRESS_STAGES[PROGRESS_STAGES.length - 1];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="spend-importer-modal"
        className="max-w-2xl bg-white border border-slate-300 text-slate-950 p-0 max-h-[92vh] overflow-hidden"
      >
        <DialogHeader className="px-6 py-4 border-b border-slate-200 bg-slate-100">
          <DialogTitle className="font-display text-lg flex items-center gap-2 text-slate-950 tracking-tight">
            <UploadSimple weight="duotone" className="text-emerald-800" size={20} />
            Import Organization Spend Data
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-700 font-mono mt-1 font-medium">
            Drop a CSV or XLSX export from SAP · Coupa · Oracle · or your ERP. The Data Hygiene Agent will normalize and the Technical Agent will UNSPSC-map.
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-auto thin-scroll max-h-[calc(92vh-80px)] p-5 space-y-4 bg-white">
          {/* ─── Drop zone ──────────────────────────────────────────── */}
          {!result && (
            <div
              data-testid="spend-importer-dropzone"
              onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              onClick={() => inputRef.current && inputRef.current.click()}
              className={`border-2 border-dashed rounded-sm p-8 text-center cursor-pointer transition-colors
                          ${dragging
                            ? "border-emerald-700 bg-emerald-50"
                            : "border-slate-300 bg-slate-50 hover:bg-slate-100 hover:border-slate-500"}`}
            >
              <FileXls weight="duotone" className="mx-auto text-emerald-800 mb-2" size={42} />
              <div className="font-display text-sm font-bold text-slate-950 tracking-tight">
                {file ? file.name : "Drop XLSX or CSV here"}
              </div>
              <div className="text-[12px] text-slate-700 font-mono font-medium mt-1">
                {file
                  ? `${(file.size / 1024 / 1024).toFixed(2)} MB · ready to import`
                  : "or click to browse · max ~50 MB · XLSX prioritized"}
              </div>
              <input
                ref={inputRef}
                type="file"
                accept=".csv,.xlsx,.xlsm,.txt"
                className="hidden"
                data-testid="spend-importer-file-input"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
            </div>
          )}

          {/* ─── Live progress narration ────────────────────────────── */}
          {running && (
            <div
              data-testid="spend-importer-progress"
              className="border border-slate-200 rounded-sm p-4 bg-slate-50"
            >
              <div className="flex items-center gap-2 mb-2">
                <Spinner weight="bold" className="text-emerald-700 animate-spin" size={16} />
                <span className="font-display text-sm font-bold text-slate-950 tracking-tight">
                  Normalizing &amp; Mapping…
                </span>
                <span className="ml-auto font-mono text-[11px] text-emerald-900 font-bold tabular-nums">
                  {stage.pct}%
                </span>
              </div>
              <div className="w-full bg-slate-200 rounded-sm overflow-hidden h-2 mb-3">
                <div
                  className="h-full bg-emerald-700 transition-all duration-500"
                  style={{ width: `${stage.pct}%` }}
                />
              </div>
              <div className="flex items-start gap-2 text-[12.5px] text-slate-950 font-medium">
                <Robot weight="duotone" className="text-slate-700 mt-0.5 shrink-0" size={14} />
                <div>
                  <span className="font-mono text-[10px] text-emerald-900 font-bold tracking-mono-label">
                    {stage.agent}
                  </span>
                  <div className="leading-snug">{stage.detail}</div>
                </div>
              </div>
            </div>
          )}

          {/* ─── Error ──────────────────────────────────────────────── */}
          {error && (
            <div className="border border-rose-300 rounded-sm p-4 bg-rose-50 text-rose-900 font-medium text-sm">
              {error}
            </div>
          )}

          {/* ─── Result ─────────────────────────────────────────────── */}
          {result && (
            <div data-testid="spend-importer-result" className="border border-emerald-300 rounded-sm bg-emerald-50/40">
              <div className="px-4 py-3 border-b border-emerald-300 flex items-center gap-2 bg-emerald-50">
                <CheckCircle weight="fill" className="text-emerald-700" size={18} />
                <span className="font-display text-sm font-bold text-emerald-900 tracking-tight">
                  Import Complete · digest committed
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200">
                <ResultCell label="Rows ingested" value={result.row_count.toLocaleString()} />
                <ResultCell label="Total spend"    value={fmtUSD(result.total_spend)} tone="emerald" />
                <ResultCell label="Unique vendors" value={result.unique_vendors.toLocaleString()} />
                <ResultCell label="UNSPSC match"   value={fmtPct(result.match_confidence_pct, 1)} tone={result.match_confidence_pct >= 80 ? "emerald" : "amber"} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-200">
                <ResultCell label="Direct"        value={fmtCompact(result.direct)} />
                <ResultCell label="Indirect"      value={fmtCompact(result.indirect)} />
                <ResultCell label="Maverick"      value={fmtCompact(result.uncategorized)} tone={result.uncategorized > 0 ? "rose" : "slate"} />
              </div>
              {result.top_vendors?.length > 0 && (
                <div className="p-4 bg-white border-t border-emerald-200">
                  <div className="font-mono text-[10px] text-slate-700 tracking-mono-label font-bold mb-2">
                    TOP COUNTERPARTIES
                  </div>
                  <ul className="space-y-1">
                    {result.top_vendors.slice(0, 5).map((v, i) => (
                      <li key={i} className="flex items-baseline justify-between text-[12.5px]">
                        <span className="text-slate-950 font-medium truncate">{v.supplier}</span>
                        <span className="font-mono text-emerald-900 font-bold tabular-nums">{fmtCompact(v.amount)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {result.agent_trail?.length > 0 && (
                <div className="p-4 bg-white border-t border-emerald-200">
                  <div className="font-mono text-[10px] text-slate-700 tracking-mono-label font-bold mb-2">
                    AGENT TRAIL
                  </div>
                  <ol className="space-y-0.5">
                    {result.agent_trail.map((t, i) => (
                      <li key={i} className="grid grid-cols-12 gap-2 text-[11.5px]">
                        <span className="col-span-4 font-mono text-emerald-900 font-bold tracking-mono-label">{t.agent}</span>
                        <span className="col-span-8 text-slate-900 font-medium leading-snug">{t.detail}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          )}

          {/* ─── Action row ─────────────────────────────────────────── */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-200">
            <button
              data-testid="spend-importer-cancel"
              onClick={() => onOpenChange(false)}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border border-slate-300 bg-white
                         hover:bg-slate-100 font-mono text-[11px] tracking-mono-label text-slate-800 font-bold transition-colors"
            >
              {result ? "Close" : "Cancel"}
            </button>
            {!result && (
              <button
                data-testid="spend-importer-run"
                onClick={runImport}
                disabled={!file || running}
                style={{
                  backgroundColor: !file || running ? "#94A3B8" : "#0D9488",
                  cursor: !file || running ? "not-allowed" : "pointer",
                  opacity: !file || running ? 0.7 : 1,
                  color: "#FFFFFF",
                  boxShadow: !file || running ? "none" : "0 2px 10px -2px rgba(6,95,70,0.55)",
                }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-sm
                           font-display text-[12px] font-bold tracking-wide transition-colors"
              >
                <UploadSimple size={13} weight="bold" />
                {running ? "Importing…" : "Run Import"}
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ResultCell({ label, value, tone = "slate" }) {
  const color = { emerald: "text-emerald-900", amber: "text-amber-900", rose: "text-rose-900", slate: "text-slate-950" }[tone];
  return (
    <div className="bg-white p-3">
      <div className="font-mono text-[10px] tracking-mono-label text-slate-700 font-bold">{label}</div>
      <div className={`font-display text-base ${color} font-bold tabular-nums mt-0.5`}>{value}</div>
    </div>
  );
}
